import { createContext, ReactElement, useContext, useState } from 'react';
import { useDealerPage } from 'components/DealerPage/hooks/useDealerPage';
import type { ISearchPageDispatch } from 'components/DealerPage/hooks/useDealerPage';
import type {
  ISearchPageAds,
  SearchPageBreadcrumbs,
  ISearchPagePaging,
} from 'api/types/searchPageApiTypes';
import { ViewType } from 'components/DealerPage/DealerPage.typed';

export type DealerPageStateType = {
  baseUrl: string;
  userId?: string | string[];
  viewType: ViewType | undefined;
  keywordSearchTerm: string;
  section: string;
  sort: string;
  paging: ISearchPagePaging;
  saved: boolean;
  page: number;
  breadcrumbs: SearchPageBreadcrumbs[];
  recommendations: ISearchPageAds[];
};
export type DealerPageDispatchType = ISearchPageDispatch & {
  setViewType: (viewType: ViewType) => void;
};

export const DealerPageDispatchContext = createContext(
  {} as DealerPageDispatchType,
);

export const DealerPageStateContext = createContext({} as DealerPageStateType);

export const useDealerPageDispatch = (): DealerPageDispatchType =>
  useContext(DealerPageDispatchContext);
export const useDealerPageState = (): DealerPageStateType =>
  useContext(DealerPageStateContext);

export const DealerPageProvider = ({
  baseUrl,
  userId,
  keywordSearchTerm = '',
  section = 'all',
  sort = '',
  paging,
  viewType,
  saved,
  initialBreadcrumbs,
  initialRecommendations = [],
  children,
}: {
  baseUrl: string;
  userId?: string | string[];
  keywordSearchTerm?: string;
  section?: string;
  sort?: string;
  paging: ISearchPagePaging;
  viewType: ViewType;
  saved: boolean;
  initialBreadcrumbs: SearchPageBreadcrumbs[];
  initialRecommendations?: ISearchPageAds[];
  children: ReactElement;
}): ReactElement => {
  const [viewTypeState, setViewType] = useState<ViewType | undefined>(viewType);
  const { state, dispatch } = useDealerPage(
    section,
    keywordSearchTerm,
    sort,
    paging,
    initialBreadcrumbs,
    initialRecommendations,
  );

  return (
    <DealerPageDispatchContext.Provider
      value={{
        setViewType,
        ...dispatch,
      }}
    >
      <DealerPageStateContext.Provider
        value={{
          baseUrl,
          userId,
          saved,
          viewType: viewTypeState,
          ...state,
        }}
      >
        {children}
      </DealerPageStateContext.Provider>
    </DealerPageDispatchContext.Provider>
  );
};
